import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, AlertTriangle, Package } from 'lucide-react';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
} from '../ui/carousel';

const PriceCard = ({ slide }) => (
  <div className="bg-white rounded-2xl border border-slate-200 p-6 max-w-[360px] mx-auto shadow-2xl">
    {slide.layout === 'detail' ? (
      <p className="text-[11px] text-slate-400 mb-3">Home / Beer / {slide.product}</p>
    ) : (
      <span className="inline-block text-[11px] font-bold text-[#2f6b4f] border border-[#2f6b4f] rounded-full px-2.5 py-0.5 mb-3">
        {slide.region}
      </span>
    )}

    <p className="text-xl font-bold text-slate-900 leading-tight">{slide.product}</p>
    {slide.sku && <p className="text-xs text-slate-400 mb-1">LCBO#: {slide.sku}</p>}
    <p className="text-sm text-slate-500 mb-4">{slide.size}</p>

    <p className="text-[15px] text-slate-600 mb-3">
      Wholesale price: <strong className="text-slate-900">{slide.wholesalePrice}</strong>
    </p>

    <div className="flex items-center justify-between gap-2 bg-[#fdf6ea] border border-amber-400 rounded-lg px-3 py-2.5 mb-4">
      <span className="text-sm text-slate-800">
        🏷️ Suggested Retail: <strong>{slide.suggestedPrice}</strong>
        {slide.savedPrice && <span className="text-[#2f6b4f] font-semibold text-[11px]"> (Saved Price)</span>}
      </span>
      <ChevronDown className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
    </div>

    <div className="space-y-1.5 mb-5">
      <div className="flex items-center justify-between text-sm">
        <span className="text-slate-500">Base Unit Cost <span className="text-slate-400">(excl. tax/dep)</span></span>
        <strong className="text-slate-900">{slide.baseCost}</strong>
      </div>
      <div className="flex items-center justify-between text-sm">
        <span className="text-[#2f6b4f] font-semibold">Profit / Unit</span>
        <strong className="text-[#2f6b4f]">{slide.profit}</strong>
      </div>
    </div>

    <p className="text-xs text-slate-500 mb-4">
      {slide.layout === 'detail' ? 'Purchasable only by case' : 'Only purchasable by case'} {slide.units}
    </p>

    <div className="w-full text-center bg-[#2f6b4f] text-white text-sm font-bold rounded-lg py-3">
      Set Your Retail Price
    </div>
  </div>
);

const AlertCard = ({ slide }) => (
  <div className="bg-white rounded-2xl border border-slate-200 p-6 max-w-[360px] mx-auto shadow-2xl">
    <span className="inline-block text-[11px] font-bold text-[#2f6b4f] border border-[#2f6b4f] rounded-full px-2.5 py-0.5 mb-3">
      {slide.region}
    </span>

    <p className="text-xl font-bold text-slate-900 leading-tight">{slide.product}</p>
    <p className="text-sm text-slate-500 mb-4">{slide.size}</p>

    <p className="text-[15px] text-slate-600 mb-1">
      Wholesale price: <strong className="text-slate-900">{slide.wholesalePrice}</strong>
    </p>
    <p className="text-xs text-slate-400 mb-3">Previous order: {slide.previousPrice}</p>

    <div className="flex items-center gap-2.5 bg-[#fdecea] border border-[#b4432e] rounded-lg px-3 py-2.5 mb-4">
      <AlertTriangle className="w-4 h-4 text-[#b4432e] flex-shrink-0" />
      <span className="text-sm text-[#b4432e] font-bold">Cost Increase +{slide.increase}</span>
    </div>

    <p className="text-sm text-slate-600 mb-5 leading-relaxed">
      MarginMate flags it before you order — so your shelf price doesn't quietly fall behind.
    </p>

    <div className="w-full text-center bg-[#2f6b4f] text-white text-sm font-bold rounded-lg py-3">
      Update Your Retail Price
    </div>
  </div>
);

const HistoryCard = ({ slide }) => (
  <div className="bg-white rounded-2xl border border-slate-200 p-6 max-w-[360px] mx-auto shadow-2xl">
    <div className="flex items-center gap-2 mb-5">
      <Package className="w-4 h-4 text-[#2f6b4f]" />
      <p className="text-sm font-bold text-slate-900">Saved Order History</p>
    </div>

    <div className="space-y-3">
      {slide.orders.map((order, i) => (
        <div key={i} className="bg-[#faf7f2] border border-slate-200 rounded-lg p-3">
          <p className="text-sm font-semibold text-slate-900 mb-1.5">{order.product}</p>
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span className="bg-white border border-slate-200 rounded px-1.5 py-0.5">SKU: {order.sku}</span>
            <span>Qty: {order.qty}</span>
            <strong className="text-slate-900">{order.cost}</strong>
          </div>
          <p className="text-[11px] text-slate-400 mt-1.5">{order.date}</p>
        </div>
      ))}
    </div>

    <p className="text-xs text-slate-500 mt-4 text-center">
      Logged automatically at checkout — nothing to write down.
    </p>
  </div>
);

const SLIDE_COMPONENTS = { price: PriceCard, alert: AlertCard, history: HistoryCard };

const MarginMateShowcase = ({ title, slides }) => {
  const [api, setApi] = useState(null);
  const [selected, setSelected] = useState(0);
  const [paused, setPaused] = useState(false);
  const sectionRef = useRef(null);
  const [inView, setInView] = useState(false);
  const [scrollOpacity, setScrollOpacity] = useState(1);

  useEffect(() => {
    if (!api) return;
    const onSelect = () => setSelected(api.selectedScrollSnap());
    api.on('select', onSelect);
    onSelect();
    return () => api.off('select', onSelect);
  }, [api]);

  useEffect(() => {
    if (!api || paused) return;
    const id = setInterval(() => api.scrollNext(), 4000);
    return () => clearInterval(id);
  }, [api, paused]);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const el = sectionRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const viewportH = window.innerHeight;

      if (rect.top >= 0) {
        setScrollOpacity(1);
        return;
      }

      const fadeDistance = rect.height * 0.9 || viewportH;
      const progress = Math.min(1, Math.abs(rect.top) / fadeDistance);
      setScrollOpacity(Math.max(0.15, 1 - progress));
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scale = 0.97 + 0.03 * scrollOpacity;

  return (
    <div
      ref={sectionRef}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      className="transition-all duration-700 ease-out"
      style={{
        opacity: inView ? scrollOpacity : 0,
        transform: `translateY(${inView ? 0 : 24}px) scale(${scale})`,
      }}
    >
      <div className="rounded-2xl glass-elevated overflow-hidden">
        <div className="flex items-center gap-2 px-5 py-4 bg-black/30 border-b border-white/5">
          <div className="w-3 h-3 rounded-full bg-red-500/60" />
          <div className="w-3 h-3 rounded-full bg-yellow-500/60" />
          <div className="w-3 h-3 rounded-full bg-green-500/60" />
          <span className="ml-4 text-sm font-medium text-slate-400">{title}</span>
        </div>

        <div className="bg-[#faf7f2] py-10 px-6 sm:px-14">
          <Carousel setApi={setApi} className="w-full">
            <CarouselContent>
              {slides.map((slide, i) => {
                const SlideComponent = SLIDE_COMPONENTS[slide.kind] || PriceCard;
                return (
                  <CarouselItem key={i}>
                    <p className="text-center text-xs font-semibold text-amber-600 uppercase tracking-wide mb-4">
                      {slide.feature}
                    </p>
                    <SlideComponent slide={slide} />
                  </CarouselItem>
                );
              })}
            </CarouselContent>
            <CarouselPrevious className="bg-white border-slate-300 text-slate-700 hover:bg-slate-100 hover:text-slate-900" />
            <CarouselNext className="bg-white border-slate-300 text-slate-700 hover:bg-slate-100 hover:text-slate-900" />
          </Carousel>

          <div className="flex items-center justify-center gap-2 mt-6">
            {slides.map((_, i) => (
              <span
                key={i}
                className={`w-1.5 h-1.5 rounded-full transition-colors ${
                  i === selected ? 'bg-[#2f6b4f]' : 'bg-slate-300'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarginMateShowcase;
